import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark, Plus, Truck } from "lucide-react";
import { useState } from "react";

import { Button, Card, Dialog, EmptyState, Pill, SkeletonRows, TextField } from "@/components/ui";
import { suppliersRepository, type Supplier } from "@/repositories/suppliersRepository";
import { useAuthStore } from "@/stores/authStore";

/**
 * The people a shop buys from.
 *
 * Mostly ordinary contact detail, with one exception that shapes the screen:
 * banking. The account number arrives from the server only for somebody holding
 * `suppliers.view_banking_details`, so the interface does not hide it — it
 * genuinely does not have it. Everybody else sees "banking details on file",
 * which is useful to know and discloses nothing.
 *
 * Changing it is a separate dialog with a reason field, because the change is
 * audited with the previous value and a reason nobody was asked for is a reason
 * nobody gives.
 */
export function SuppliersPage() {
  const [adding, setAdding] = useState(false);
  const [banking, setBanking] = useState<Supplier | null>(null);

  const canManage = useAuthStore((s) => s.can("suppliers.manage"));
  const canSeeBanking = useAuthStore((s) => s.can("suppliers.view_banking_details"));
  const queryClient = useQueryClient();

  const suppliers = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => suppliersRepository.list(),
  });

  const rows = suppliers.data ?? [];

  function refresh() {
    setAdding(false);
    setBanking(null);
    void queryClient.invalidateQueries({ queryKey: ["suppliers"] });
  }

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus size={16} aria-hidden /> Add supplier
          </Button>
        </div>
      )}

      <Card>
        {suppliers.isLoading ? (
          <SkeletonRows rows={6} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Truck}
            title="No suppliers yet"
            description="Add the cash-and-carry, the bread van and anybody else the shop buys from."
          />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((supplier) => (
              <li key={supplier.id} className="flex flex-wrap items-center gap-3 py-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-ink-900">{supplier.name}</span>
                  <span className="block text-xs text-ink-500">
                    {supplier.contact_name || supplier.phone || "No contact on file"}
                    {supplier.payment_terms_days > 0
                      ? ` · ${supplier.payment_terms_days} days to pay`
                      : " · Cash on delivery"}
                  </span>
                </span>

                {supplier.has_banking_details &&
                  (canSeeBanking && supplier.bank_account_number ? (
                    <span className="text-xs tabular-nums text-ink-600">
                      {supplier.bank_name} ····{supplier.bank_account_number.slice(-4)}
                    </span>
                  ) : (
                    // Deliberately not a masked number: the field is absent from
                    // the payload, and pretending otherwise would imply the
                    // browser has it.
                    <Pill tone="neutral">Banking on file</Pill>
                  ))}

                {canSeeBanking && (
                  <Button variant="ghost" size="sm" onClick={() => setBanking(supplier)}>
                    <Landmark size={14} aria-hidden /> Banking
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {adding && <NewSupplierDialog onClose={() => setAdding(false)} onCreated={refresh} />}
      {banking && (
        <BankingDialog supplier={banking} onClose={() => setBanking(null)} onSaved={refresh} />
      )}
    </div>
  );
}

function NewSupplierDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [phone, setPhone] = useState("");
  const [terms, setTerms] = useState("0");

  const create = useMutation({
    mutationFn: () =>
      suppliersRepository.create({
        name: name.trim(),
        contact_name: contact.trim(),
        phone: phone.trim(),
        payment_terms_days: Number(terms) || 0,
      }),
    onSuccess: onCreated,
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add a supplier"
      description="Banking details are added separately, so the change is recorded."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={name.trim().length === 0}
            onClick={() => create.mutate()}
          >
            Add supplier
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <TextField
          label="Name"
          value={name}
          maxLength={150}
          onChange={(event) => setName(event.target.value)}
          placeholder="Katlehong Cash & Carry"
        />
        <TextField
          label="Contact person"
          value={contact}
          maxLength={120}
          onChange={(event) => setContact(event.target.value)}
        />
        <TextField
          label="Phone number"
          value={phone}
          maxLength={20}
          inputMode="tel"
          onChange={(event) => setPhone(event.target.value)}
        />
        <TextField
          label="Days to pay"
          value={terms}
          inputMode="numeric"
          className="w-28"
          onChange={(event) => setTerms(event.target.value)}
          hint="Zero means cash on delivery, which is how most spaza buying works."
        />

        {create.error != null && (
          <p className="text-sm text-danger-700">
            {create.error instanceof Error ? create.error.message : "That could not be saved."}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/**
 * Where this supplier gets paid.
 *
 * A reason is asked for and shown as required, because the audit entry keeps the
 * previous account number and a change with no explanation next to it is the
 * shape of the attack this exists to catch.
 */
function BankingDialog({
  supplier,
  onClose,
  onSaved,
}: {
  supplier: Supplier;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [bank, setBank] = useState(supplier.bank_name ?? "");
  const [number, setNumber] = useState(supplier.bank_account_number ?? "");
  const [branch, setBranch] = useState(supplier.bank_branch_code ?? "");
  const [reason, setReason] = useState("");

  const save = useMutation({
    mutationFn: () =>
      suppliersRepository.setBanking(supplier.id, {
        bank_name: bank.trim(),
        bank_account_number: number.trim(),
        bank_branch_code: branch.trim(),
        reason: reason.trim(),
      }),
    onSuccess: onSaved,
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Banking — ${supplier.name}`}
      description="Every change is recorded, with what the account number used to be."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={reason.trim().length === 0}
            onClick={() => save.mutate()}
          >
            Save banking details
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <TextField label="Bank" value={bank} onChange={(event) => setBank(event.target.value)} />
        <TextField
          label="Account number"
          value={number}
          inputMode="numeric"
          onChange={(event) => setNumber(event.target.value)}
        />
        <TextField
          label="Branch code"
          value={branch}
          inputMode="numeric"
          className="w-36"
          onChange={(event) => setBranch(event.target.value)}
        />
        <TextField
          label="Why is this changing?"
          value={reason}
          maxLength={200}
          onChange={(event) => setReason(event.target.value)}
          placeholder="They moved banks — confirmed on their letterhead"
        />

        {save.error != null && (
          <p className="text-sm text-danger-700">
            {save.error instanceof Error ? save.error.message : "That could not be saved."}
          </p>
        )}
      </div>
    </Dialog>
  );
}
